<template>
    <fieldset :class="['vfb-fieldset', head.cssClass || ''].filter(Boolean).join(' ')">
        <legend v-if="head.legend" class="vfb-fieldset__legend">{{ head.legend }}</legend>

        <div v-for="(instance, position) in instances" :key="instance.id"
             class="vfb-fieldset__instance">
            <!-- Felder in eigener Grid-Zeile (flex:1); Remove daneben als schmaler
                 Flex-Sibling (Inhaltsbreite, vertikal zentriert). Bewusst NICHT im
                 selben Grid → Remove versetzt die Außen-Spalten nicht weiter, dafür
                 fluchten die Repeater-Spalten nicht mit den Außen-Spalten (akzeptiert). -->
            <div class="row gx-4 gy-4 vfb-fieldset__instance-fields">
                <div v-for="member in members" :key="member.index"
                     :class="'col-md-' + (member.element.columnSize || 12)" class="col-12 vfb__column">
                    <div class="vfb__element">
                        <component
                            :is="resolveComponent(member.element.type)"
                            v-if="resolveComponent(member.element.type)"
                            v-bind="buildMemberProps(member, position, head.name)"
                            @update:modelValue="(value) => onMemberUpdate(member, position, value, head.name)"
                        />
                    </div>
                </div>
            </div>

            <!-- Remove pro Instanz: an der letzten Feld-Zeile ausgerichtet. Zeigt den
                 (übersetzbaren) Text, wenn ein removeLabel gesetzt ist, sonst das
                 ×-Icon. Wird AUSGEBLENDET, sobald nur noch minInstances übrig sind
                 (canRemove false) — kein disabled-Zustand mehr. -->
            <div v-if="members.length > 0 && canRemove" class="vfb-fieldset__instance-actions">
                <button type="button" class="btn btn-outline-secondary btn-sm vfb-fieldset__remove"
                        :aria-label="removeAriaLabel" :title="removeAriaLabel"
                        @click="removeInstance(position)">
                    <span v-if="removeText">{{ removeText }}</span>
                    <span v-else aria-hidden="true">&times;</span>
                </button>
            </div>
        </div>

        <!-- sr-only proxy input for fieldsetMinCount validation (only when minInstances > 1).
             type="text" + position:absolute keeps offsetParent !== null so v3 validates it.
             Styling siehe .vfb-visually-hidden in assets/vfb-base-styling.scss. -->
        <div v-if="effectiveMin > 1" class="js-form-validation__group">
            <input
                ref="countProxyRef"
                type="text"
                :value="String(instanceCount)"
                :data-validation-rules="countValidationRules"
                tabindex="-1"
                aria-hidden="true"
                class="vfb-visually-hidden"
            />
            <div class="js-form-validation__errors"></div>
        </div>

        <!-- Der globale Action-Bereich enthält nur noch den Add-Button; das
             Entfernen läuft pro Instanz (siehe oben). Nur sichtbar, wenn es
             Felder zu wiederholen gibt und Hinzufügen erlaubt ist. -->
        <div v-if="members.length > 0 && headCanAdd" class="vfb-fieldset__actions">
            <button type="button" class="btn btn-outline-primary btn-sm vfb-fieldset__add"
                    :aria-label="addAriaLabel" :title="addAriaLabel"
                    :disabled="!canAdd" @click="addInstance">
                <span v-if="addText">{{ addText }}</span>
                <span v-else aria-hidden="true">+</span>
            </button>
        </div>
    </fieldset>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';

interface Member {
    element: any;
    index: number;
}

interface Props {
    head: any;
    members: Member[];
    resolveComponent: (type: string) => any;
    buildMemberProps: (member: Member, instanceIndex: number, fieldsetName: string) => any;
    onMemberUpdate: (member: Member, instanceIndex: number, value: any, fieldsetName: string) => void;
    onInstancesChanged: () => void;
    onInstanceRemoved: (fieldsetName: string, removedPosition: number) => void;
    addLabelFallback?: string;
    removeLabelFallback?: string;
}

const props = withDefaults(defineProps<Props>(), {
    addLabelFallback: 'Add',
    removeLabelFallback: 'Remove',
});

const toInt = (value: unknown, fallback: number) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : fallback;
};

// minInstances 0 is allowed (an optional repeater, e.g. one gated by a showWhen
// condition, may legitimately start with — and be reduced to — zero instances).
const effectiveMin = computed(() => Math.max(0, toInt(props.head.minInstances, 1)));
// maxInstances 0 (or unset) means unbounded.
const effectiveMax = computed(() => {
    const max = toInt(props.head.maxInstances, 0);
    return max > 0 ? Math.max(effectiveMin.value, max) : Number.POSITIVE_INFINITY;
});

const defaultInstances = computed(() => {
    const d = toInt(props.head.defaultInstances, 0);
    // defaultInstances is coupled to minInstances: effectiveMin is both the floor
    // and the fallback, effectiveMax the hard cap. A schema value below min is
    // raised to min (the editor clamps too; this guards direct/legacy data).
    const capped = d > 0 ? Math.min(d, effectiveMax.value) : effectiveMin.value;
    return Math.max(effectiveMin.value, capped);
});

// Repeater-Instanzen werden über ein Array stabiler IDs getrieben (nicht über
// einen Zähler), damit ein Remove eine BELIEBIGE Instanz entfernen kann und der
// stabile :key="instance.id" die DOM-Knoten der überlebenden Instanzen erhält.
// Der Bracket-Index in der Submission ist die ARRAY-POSITION (nicht die id), so
// bleiben die Namen persons[0..n-1] nach jedem Remove contiguous.
interface Instance {
    id: number;
}
const instances = ref<Instance[]>(
    Array.from({ length: defaultInstances.value }, (_, i) => ({ id: i }))
);
let nextInstanceId = defaultInstances.value;

// Beibehalten als computed, damit der count-Proxy (fieldsetMinCount-Validierung)
// und canAdd/canRemove unverändert auf instanceCount referenzieren können.
const instanceCount = computed(() => instances.value.length);

const headCanAdd = computed(() => props.head.canAdd !== false);
const headCanRemove = computed(() => props.head.canRemove !== false);
const canAdd = computed(() => headCanAdd.value && instances.value.length < effectiveMax.value);
const canRemove = computed(() => headCanRemove.value && instances.value.length > effectiveMin.value);

// Eigener (getrimmter) Add-Text, falls konfiguriert — sonst leer → "+"-Icon.
const addText = computed(() => (props.head.addLabel || '').trim());
// aria-label/title immer beschreibend: eigener Text oder übersetzter Fallback.
const addAriaLabel = computed(() => addText.value || props.addLabelFallback);

// Remove zeigt den getrimmten Text, wenn gesetzt — sonst das ×-Icon.
const removeText = computed(() => (props.head.removeLabel || '').trim());
// aria-label/title immer beschreibend: eigener Text oder übersetzter Fallback.
const removeAriaLabel = computed(() => removeText.value || props.removeLabelFallback);

const countProxyRef = ref<HTMLInputElement | null>(null);
const countValidationRules = computed(() =>
    JSON.stringify([{ fieldsetMinCount: effectiveMin.value }])
);

watch(instanceCount, () => {
    nextTick(() => {
        countProxyRef.value?.dispatchEvent(new Event('input', { bubbles: true }));
    });
});

// Hinzufügen hängt eine neue Instanz mit frischer, monoton wachsender id ans
// Ende. Die Array-Position bestimmt den Bracket-Index, daher bleiben die Namen
// contiguous.
const addInstance = () => {
    if (!canAdd.value) {
        return;
    }
    instances.value.push({ id: nextInstanceId++ });
    // Re-scan the DOM so form-validation v3 picks up the new instance's inputs.
    nextTick(() => props.onInstancesChanged());
};

// Entfernt GENAU die Instanz an `position`. Das Re-Keying der Feldwerte
// passiert im Parent (onInstanceRemoved), bevor v3 neu scannt — so rückt der
// Wert der überlebenden höheren Instanzen korrekt auf den neuen Index nach.
const removeInstance = (position: number) => {
    if (!canRemove.value) {
        return;
    }
    instances.value.splice(position, 1);
    props.onInstanceRemoved(props.head.name, position);
    nextTick(() => props.onInstancesChanged());
};
</script>

